/**
 * MER-693: tell the reader when a window runs past the end of the feed,
 * instead of letting the dashboard read as a business collapse.
 *
 * THE DEFECT. Every window on this dashboard is measured against TODAY. The
 * work-order feed is not: it lands from an ERM export and trails by however
 * long it has been since the last one. Whenever that export is late, three
 * things go wrong at once. The measured sizes are recorded on the ticket and
 * deliberately not here: customer figures do not belong in source.
 *
 *   1. Count tiles declared `this_week` asked for a week that began AFTER the
 *      feed ended. Zero rows is an arithmetically correct answer to the
 *      question asked, and the tile said "Limited activity this period. Try a
 *      wider range." That sentence names the wrong cause -- the period was
 *      not quiet, it was unreported -- and sends an owner off to widen a
 *      range when the remedy is an export.
 *   2. `this_month` charts drew the handful of days of the month that had
 *      landed as though they were the month.
 *   3. Worst, every tile carrying `trend: vs_prior_period` compared a
 *      PART-POPULATED current window against a FULL prior one and published
 *      the shortfall as a business result. Sliding both windows back so they
 *      END at the feed's last day reverses the sign of that comparison. The
 *      arrow was not measuring the business. It was measuring how long ago
 *      the export ran.
 *
 * THE RULE. This is the partial-vs-full trap priorWindow.mjs already refuses
 * for period-bucketed tables, arriving by a different road. There the partial
 * bucket is a calendar fact; here the partial window is a feed fact, invisible
 * from the manifest and knowable only by asking the data how far it goes. So
 * we ask, once per source per page, and compare the answer to the last day the
 * widget's window includes:
 *
 *   - SHORTFALL WITHIN A DAY: nothing happens at all. A feed that ended
 *     yesterday is a feed that is working, tonight's export has simply not run
 *     yet, and a note on every trailing tile every single day would train the
 *     reader to ignore it.
 *   - SHORTFALL BEYOND A DAY: the tile carries "Feed through <Mon D>" so the
 *     number it DOES show is read against the right period. The value itself
 *     is never suppressed: those events really happened.
 *   - SHORTFALL MATERIAL AGAINST THE WINDOW'S OWN WIDTH: the vs-prior
 *     percentage is withheld as well, and the tile says so.
 *
 * WHY THE LAST ONE IS PROPORTIONAL AND NOT A FLAT NUMBER OF DAYS. The same
 * lag is a different fact on a different window. Two weeks missing from a
 * seven-day window leaves nothing of it; two weeks missing from a twelve-month
 * window moves the total by a few percent, which is well inside the swing
 * deltaPct already tolerates. A flat rule silences both, and the second
 * silence has a cost: while the feed trails, volume that is genuinely falling
 * becomes indistinguishable from the lag, and the dashboard stops answering
 * rather than answering wrong. So the delta is withheld only when the
 * unreported days are more than FEED_LAG_MATERIAL_SHARE of the window the
 * tile asked for. That is the same shape of judgement priorWindow.mjs makes
 * when it decides a prior window is comparable.
 *
 * WHAT THE DATE ON THE FACE MEANS. It is the newest row the SOURCE holds for
 * this tenant, not the newest row behind this particular tile: the probe is
 * one read per (table, timeline column) for the whole page and carries no
 * widget filter. A tile filtered to one equipment type can therefore sit
 * beside a feed date a day or two newer than its own last row. That is why
 * the copy says "Feed through" and not "Data through": it is a statement
 * about the export, which is true for every tile drawn from it, rather than a
 * claim about the slice, which would not be. Scoping the probe per filter
 * combination would multiply the reads by the number of slices on the page
 * and is not worth it for a date that moves a day.
 *
 * A window that ends on or before the feed's last day changes nothing at all.
 * That covers a completed month token (MER-692), `last_month`, and any
 * trailing window on a feed that is current, which is the state we want and
 * the state this module must stay silent in.
 *
 * WHAT THIS IS NOT. It does not re-anchor any window to the feed end. Every
 * tile still answers the question it was asked, over the window it declares;
 * this module only says what the answer rests on. Anchoring the whole
 * dashboard to the feed is a much larger behaviour change and AJ's call.
 *
 * Pure and dependency-free so node:test imports it directly (the
 * nowCutoff.mjs / priorWindow.mjs precedent); feedLag.d.mts types it for
 * WidgetRenderer and queries.ts. All calendar math runs on LOCAL date parts
 * and never toISOString, so a UTC offset cannot move a boundary by a day
 * (nowCutoffIsoBound's convention).
 */

import { parseMonthToken } from "./calendarMonth.mjs";

/**
 * Trailing window widths in days, mirroring TIMELINE_DAY_RANGES in queries.ts
 * and TRAILING_DAY_RANGES in priorWindow.mjs. Membership decides whether a
 * range ends at today (applyTimeline's trailing branch emits a lower bound and
 * NO upper bound, so every range in this table runs to the present); the width
 * decides whether a given shortfall is material against it. An unknown range
 * is answered with "no opinion" rather than a guess.
 */
const TRAILING_RANGE_DAYS = {
  this_week: 7,
  last_week: 7,
  last_30_days: 30,
  last_90_days: 90,
  last_180_days: 180,
  ytd: 365,
  last_4_weeks: 28,
  last_4_months: 122,
  last_6_months: 183,
  last_12_months: 366,
  last_quarter: 92,
  last_year: 366,
};

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** An ISO calendar date, which is all we ever read off a timeline column. */
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Days of slack before a trailing feed is worth naming at all. One: a feed
 * that ended yesterday is a feed that is working.
 */
export const FEED_LAG_GRACE_DAYS = 1;

/**
 * The share of its own window a shortfall has to exceed before the vs-prior
 * percentage is withheld. A tenth: below that the missing days move a total
 * by less than the swing a delta already absorbs without meaning anything,
 * and withholding there would cost the reader a real signal to remove a
 * distortion smaller than the noise.
 */
export const FEED_LAG_MATERIAL_SHARE = 0.1;

/** What the tile says in place of the arrow it is not drawing. */
export const DELTA_WITHHELD_NOTE = "vs prior withheld";

function startOfDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function parseIsoDate(value) {
  if (typeof value !== "string") return null;
  const date = value.slice(0, 10);
  if (!ISO_DATE_RE.test(date)) return null;
  const y = Number(date.slice(0, 4));
  const m = Number(date.slice(5, 7));
  const d = Number(date.slice(8, 10));
  const out = new Date(y, m - 1, d);
  // Round-trip guard: Date silently normalizes 2026-02-31 into March.
  if (out.getFullYear() !== y || out.getMonth() !== m - 1 || out.getDate() !== d) return null;
  return out;
}

function isoDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * Whole days from `a` to `b`. Compares UTC midnights built from LOCAL calendar
 * parts, so a DST transition inside the span cannot round the answer off
 * (periodWindow.mjs uses the same trick).
 */
function dayDiff(a, b) {
  const ua = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const ub = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.round((ub - ua) / 86400000);
}

/** Last day of the month `d` falls in. Day 0 of the next month, leap-safe. */
function endOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

/** First day of the calendar month a month token names, or null. */
function monthStart(range) {
  const month = parseMonthToken(range);
  if (!month) return null;
  return new Date(Number(month.slice(0, 4)), Number(month.slice(5, 7)) - 1, 1);
}

/**
 * The LAST DAY a widget's window includes, as an ISO date, or null when this
 * module has no opinion (no timeline, snapshot mode, or a range it does not
 * recognize). Mirrors applyTimeline's window semantics in queries.ts:
 *
 *   month:YYYY-MM          the last day of that calendar month
 *   period + this_month    the last day of the month in progress, which is in
 *                          the FUTURE for most of every month -- that is the
 *                          whole reason a this_month chart reads near-empty
 *                          while the feed trails
 *   period + last_month    the last day of the previous calendar month
 *   trailing range         today. applyTimeline's trailing branch sets only a
 *                          lower bound, so the window runs to the present.
 *
 * `this_week` is deliberately a trailing range here and not a calendar one,
 * because that is what applyTimeline does with it: `mode: period` only
 * special-cases this_month / last_month, and every other range falls through
 * to the trailing branch.
 */
export function windowEndFor(timeline, today) {
  if (!timeline || !timeline.column) return null;
  if (timeline.mode === "snapshot") return null;
  const range = timeline.range;

  const named = monthStart(range);
  if (named) return isoDate(endOfMonth(named));
  if (timeline.mode === "period" && range === "this_month") {
    return isoDate(endOfMonth(today));
  }
  if (timeline.mode === "period" && range === "last_month") {
    return isoDate(endOfMonth(new Date(today.getFullYear(), today.getMonth() - 1, 1)));
  }
  if (range && Object.prototype.hasOwnProperty.call(TRAILING_RANGE_DAYS, range)) {
    return isoDate(startOfDay(today));
  }
  return null;
}

/**
 * How many days wide that window is, or null when unknown. The calendar
 * branches answer with the real length of the month they name (28 to 31);
 * the trailing branches answer with the declared width applyTimeline
 * subtracts, so the two sides of the ratio below are measured the same way
 * the query measured them.
 */
export function windowDaysFor(timeline, today) {
  if (!timeline || !timeline.column) return null;
  if (timeline.mode === "snapshot") return null;
  const range = timeline.range;

  const named = monthStart(range);
  if (named) return endOfMonth(named).getDate();
  if (timeline.mode === "period" && range === "this_month") {
    return endOfMonth(today).getDate();
  }
  if (timeline.mode === "period" && range === "last_month") {
    return endOfMonth(new Date(today.getFullYear(), today.getMonth() - 1, 1)).getDate();
  }
  if (range && Object.prototype.hasOwnProperty.call(TRAILING_RANGE_DAYS, range)) {
    return TRAILING_RANGE_DAYS[range];
  }
  return null;
}

/**
 * 'Feed through Sep 2' for an ISO date, or null when it is not one. The date
 * is the newest row the SOURCE holds, so the copy names the feed and not the
 * tile: see the note at the top of this file about why a per-tile claim would
 * not be true on a filtered widget.
 */
export function dataThroughNote(dataThrough) {
  const d = parseIsoDate(dataThrough);
  if (!d) return null;
  return `Feed through ${MONTH_NAMES[d.getMonth()]} ${d.getDate()}`;
}

/**
 * Classify the one-row read of a source's newest timeline value (the probe in
 * feedLagData.ts). Kept pure so node:test drives it with no Supabase.
 *
 *   { dataThrough: 'YYYY-MM-DD', problem: null }  a usable date
 *   { dataThrough: null, problem: <text> }        an error reply, no rows, or
 *                                                 a value that is not a date.
 *                                                 The caller logs the problem
 *                                                 to the console and leaves
 *                                                 every tile exactly as it is.
 */
export function classifyDataThrough(result, column) {
  if (!result || typeof result !== "object") {
    return { dataThrough: null, problem: "no reply" };
  }
  if (result.error) {
    const message =
      result.error && typeof result.error.message === "string" && result.error.message
        ? result.error.message
        : "read failed";
    return { dataThrough: null, problem: message };
  }
  const rows = Array.isArray(result.data) ? result.data : [];
  if (rows.length === 0) return { dataThrough: null, problem: "no rows" };
  const row = rows[0];
  const raw = row && typeof row === "object" ? row[column] : undefined;
  const parsed = parseIsoDate(raw);
  if (!parsed) return { dataThrough: null, problem: `${column} is not an ISO date` };
  return { dataThrough: isoDate(parsed), problem: null };
}

/**
 * The render decision, kept out of the component so it is directly testable.
 *
 * @param {{
 *   timeline: object | null | undefined,
 *   periodGrain?: boolean,
 *   dataThrough?: string | null,
 *   today: Date,
 * }} opts
 *   `periodGrain` is true for kpi_summary / route_kpis / financial_health.
 *   Those rows ARE periods, useWidgetQuery already refuses their vs-prior
 *   pass outright (priorWindow.mjs's note), and periodWindow.mjs already
 *   ends their trailing windows at the last COMPLETE bucket. Applying this
 *   guard on top would be a second answer to a question already answered.
 *   `dataThrough` is the probe's answer: undefined while it is in flight,
 *   null when it failed or the source is empty.
 *   `timeline` must be the EFFECTIVE timeline the query ran, not the manifest
 *   one. They differ in demo mode, where applyTimeline rewrites every range
 *   to ytd; a guard reading the declared range there would compute a window
 *   the tile never asked for and stay silent on the one it did.
 *
 * @returns {{ stale: boolean, suppressDelta: boolean, dataThrough: string,
 *   windowEnd: string, windowDays: number | null, shortfallDays: number,
 *   note: string, deltaNote: string | null } | null}
 *   null means "change nothing", which is the answer for a fresh feed, an
 *   unknown data-through, a period-grain source, a snapshot, and any window
 *   this module cannot bound.
 */
export function resolveFeedLag(opts = {}) {
  const { timeline, periodGrain = false, dataThrough, today } = opts;
  if (periodGrain) return null;
  const through = parseIsoDate(dataThrough);
  if (!through) return null;
  const now = today ?? new Date();
  const windowEnd = windowEndFor(timeline, now);
  const end = parseIsoDate(windowEnd);
  if (!end) return null;
  const shortfallDays = dayDiff(through, end);
  if (shortfallDays <= FEED_LAG_GRACE_DAYS) return null;
  const windowDays = windowDaysFor(timeline, now);
  // An unbounded window is treated as materially short: we would rather
  // withhold a percentage we cannot vouch for than publish one.
  const suppressDelta = !windowDays || shortfallDays / windowDays > FEED_LAG_MATERIAL_SHARE;
  const iso = isoDate(through);
  return {
    stale: true,
    suppressDelta,
    dataThrough: iso,
    windowEnd,
    windowDays: windowDays ?? null,
    shortfallDays,
    note: dataThroughNote(iso),
    deltaNote: suppressDelta ? DELTA_WITHHELD_NOTE : null,
  };
}

// ---------------------------------------------------------------------------
// FEED_INCOMPLETE: the pipeline's own verdict on a starved invoice feed.
//
// normalize.py stamps kpi_summary.data_quality = 'FEED_INCOMPLETE' on a
// FINISHED period whose invoice line count came in at or under a fraction of
// its own trailing median, corroborated across the earned and billed clocks
// (_invoice_starved_flag). It is the same class of fact as the lag above --
// the period is real, the reporting of it is short -- and it needs the
// opposite treatment depending on what a tile does with the row:
//
//   SUM and COUNT keep the row. A partial week's events and dollars are real
//     data. Dropping them would understate a total by more than the starved
//     row does, and would silently move a figure an owner reconciles.
//   AVERAGES and RATIOS drop it. A mean over periods weights a one-fifth-
//     reported week exactly like a full one, and a SUM(num)/SUM(denom) ratio
//     takes a numerator that arrived from the starved feed against a
//     denominator that did not. Both answer with a number that is not about
//     the business.
//
// NULL-SAFETY IS THE WHOLE TRICK. A bare `.neq('data_quality', ...)` is NULL-
// unsafe: in SQL every comparison against NULL is unknown, so a row with no
// verdict -- which is most rows -- fails the predicate and is excluded. That
// is what blanked every good row in the MER-687 probe. The `or(is.null, neq)`
// form is the fix and the only form this module will emit.
// ---------------------------------------------------------------------------

/** The marker normalize.py stamps. Mirrored from the pipeline, not imported. */
export const FEED_INCOMPLETE = "FEED_INCOMPLETE";

/** The column it is stamped on. */
export const DATA_QUALITY_COLUMN = "data_quality";

/**
 * Relations that carry `data_quality`. Verified read-only against production:
 * kpi_summary has the column; route_kpis, financial_health and service_event
 * do not, and filtering one of them on it would 42703 the request and render
 * the awaiting-source-data gate over real numbers.
 */
export const FEED_QUALITY_TABLES = new Set(["kpi_summary"]);

/**
 * Aggregation shapes that must not average a starved period in. `avg` is the
 * manifest aggregation; the ratio values name the query-layer paths that
 * divide one sum by another (DERIVED_RATIOS), fold a stored ratio against its
 * own weight column (WEIGHTED_RATIOS) or read one stored ratio row
 * (UNCOMBINABLE_RATIOS). The last replaced an `avg` read (MER-684), so it
 * keeps the exclusion that read had.
 */
const AVERAGING_SHAPES = new Set(["avg", "derived_ratio", "weighted_ratio", "single_row_ratio"]);

/**
 * Should this read exclude FEED_INCOMPLETE rows? True only where the answer
 * is an average or a ratio AND the relation actually has the column.
 */
export function excludesFeedIncomplete(opts = {}) {
  const { table, shape } = opts;
  if (!FEED_QUALITY_TABLES.has(String(table))) return false;
  return AVERAGING_SHAPES.has(String(shape));
}

/**
 * The NULL-safe PostgREST `or` body that keeps every unstamped row and drops
 * only the starved ones. Never a bare neq: see the note above.
 */
export function feedIncompleteOrClause() {
  return `${DATA_QUALITY_COLUMN}.is.null,${DATA_QUALITY_COLUMN}.neq.${FEED_INCOMPLETE}`;
}
